import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  MapPin, Star, CheckCircle2, ArrowRight, Users, Shield, Clock,
  Sparkles, Search
} from "lucide-react";

// New Jersey cities for SEO
const POPULAR_CITIES = [
  "Newark", "Jersey City", "Paterson", "Elizabeth", "Edison",
  "Woodbridge", "Lakewood", "Toms River", "Hamilton", "Trenton",
  "Clifton", "Camden", "Brick", "Cherry Hill", "Passaic",
  "Union City", "Old Bridge", "Middletown", "East Orange", "Bayonne",
  "North Bergen", "Vineland", "Perth Amboy", "Plainfield", "West New York"
];

// Service display names
const SERVICE_NAMES = {
  plumbers: "Plumbers",
  electricians: "Electricians",
  cleaners: "House Cleaners",
  painters: "Painters",
  landscapers: "Landscapers",
  movers: "Movers",
  handymen: "Handymen",
  mechanics: "Auto Mechanics",
  roofers: "Roofers",
  hvac: "HVAC Technicians",
  carpenters: "Carpenters",
  locksmiths: "Locksmiths"
};

// Category mapping for API
const SERVICE_TO_CATEGORY = {
  plumbers: "plumbing",
  electricians: "electrical",
  cleaners: "cleaning",
  painters: "painting",
  landscapers: "landscaping",
  movers: "moving",
  handymen: "handyman",
  mechanics: "mobile_mechanic",
  roofers: "roofing",
  hvac: "hvac",
  carpenters: "carpentry",
  locksmiths: "locksmith"
};

export default function LocalServicePage() {
  const { service, city } = useParams();
  const navigate = useNavigate();
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const serviceName = SERVICE_NAMES[service] || service;
  const cityName = city?.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || "";
  const categoryId = SERVICE_TO_CATEGORY[service] || service;

  useEffect(() => {
    const fetchProfessionals = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/professionals/public`, {
          params: {
            category: categoryId,
            location: cityName,
            limit: 6
          }
        });
        setProfessionals(response.data.professionals || []);
        setTotalCount(response.data.total || 0);
      } catch (error) {
        setProfessionals([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProfessionals();
  }, [categoryId, cityName]);

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Best ${serviceName} in ${cityName} - BQL`,
    "description": `Find trusted ${serviceName.toLowerCase()} in ${cityName}. Get free quotes from background-checked professionals.`,
    "areaServed": {
      "@type": "City",
      "name": cityName
    },
    "serviceType": serviceName
  };

  return (
    <div className="min-h-screen bg-sand">
      {/* SEO Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      <Header />

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 bg-gradient-to-b from-forest/10 to-sand">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="bg-forest/10 text-forest mb-4">
            <MapPin className="w-3 h-3 mr-1" /> {cityName}
          </Badge>
          
          <h1 className="font-heading font-bold text-3xl md:text-5xl text-gray-900 mb-4">
            Best {serviceName} in {cityName}
          </h1>
          
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Find trusted, background-checked {serviceName.toLowerCase()} near you. 
            Compare quotes and hire with confidence.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register?type=customer">
              <Button size="lg" className="bg-sienna hover:bg-sienna-dark rounded-full px-8">
                <Search className="w-4 h-4 mr-2" />
                Post a Job Free
              </Button>
            </Link>
            <Link to={`/professionals?category=${categoryId}&location=${encodeURIComponent(cityName)}`}>
              <Button size="lg" variant="outline" className="rounded-full px-8 border-forest text-forest hover:bg-forest/5">
                Browse All {serviceName}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 px-4 bg-white border-y border-stone-200">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <Shield className="w-8 h-8 text-forest mb-2" />
              <p className="font-medium text-gray-900">Background Checked</p>
              <p className="text-sm text-gray-500">Verified professionals</p>
            </div>
            <div className="flex flex-col items-center">
              <Star className="w-8 h-8 text-yellow-500 mb-2" />
              <p className="font-medium text-gray-900">Top Rated</p>
              <p className="text-sm text-gray-500">5-star reviews</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-8 h-8 text-sienna mb-2" />
              <p className="font-medium text-gray-900">Fast Response</p>
              <p className="text-sm text-gray-500">Within 24 hours</p>
            </div>
            <div className="flex flex-col items-center">
              <Users className="w-8 h-8 text-forest mb-2" />
              <p className="font-medium text-gray-900">{totalCount}+ Pros</p>
              <p className="text-sm text-gray-500">In {cityName}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Professionals Grid */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading font-bold text-2xl text-gray-900 mb-6">
            <Sparkles className="w-5 h-5 inline mr-2 text-sienna" />
            Top {serviceName} in {cityName}
          </h2>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : professionals.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {professionals.map((pro) => (
                <Card key={pro.id} className="hover:shadow-lg transition-shadow border-stone-200">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-gray-900">
                          {pro.business_name || pro.full_name}
                        </h3>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {pro.service_area || cityName}
                        </p>
                      </div>
                      {pro.background_check_status === "approved" && (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Verified
                        </Badge>
                      )}
                    </div>

                    {pro.average_rating > 0 && (
                      <div className="flex items-center gap-1 mb-3">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="font-medium">{pro.average_rating.toFixed(1)}</span>
                        <span className="text-gray-500 text-sm">
                          ({pro.review_count || 0} reviews)
                        </span>
                      </div>
                    )}

                    {pro.bio && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{pro.bio}</p>
                    )}

                    <Link to={`/professional/${pro.id}`}>
                      <Button className="w-full bg-forest hover:bg-forest-dark rounded-full">
                        View Profile <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-stone-200">
              <CardContent className="p-12 text-center">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-xl text-gray-900 mb-2">
                  No {serviceName} found in {cityName} yet
                </h3>
                <p className="text-gray-600 mb-6">
                  Be the first to post a job and we'll match you with qualified professionals.
                </p>
                <Link to="/register?type=customer">
                  <Button className="bg-sienna hover:bg-sienna-dark rounded-full">
                    Post a Job Free
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {professionals.length > 0 && totalCount > 6 && (
            <div className="text-center mt-8">
              <Link to={`/professionals?category=${categoryId}&location=${encodeURIComponent(cityName)}`}>
                <Button variant="outline" className="rounded-full border-forest text-forest hover:bg-forest/5">
                  View All {totalCount} {serviceName} in {cityName}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading font-bold text-2xl text-gray-900 mb-8 text-center">
            Why Choose BQL for {serviceName} in {cityName}?
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-forest" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Verified Professionals</h3>
              <p className="text-gray-600">
                All {serviceName.toLowerCase()} are background-checked and verified for your safety.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-sienna/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-sienna" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Compare Quotes</h3>
              <p className="text-gray-600">
                Receive multiple quotes from top {serviceName.toLowerCase()} and choose the best fit.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-forest" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Free to Post</h3>
              <p className="text-gray-600">
                Post your job for free and let qualified {serviceName.toLowerCase()} come to you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Other Cities */}
      <section className="py-12 px-4 bg-sand">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading font-bold text-xl text-gray-900 mb-6">
            Find {serviceName} in Other Cities
          </h2>
          <div className="flex flex-wrap gap-2">
            {POPULAR_CITIES.filter(c => c.toLowerCase().replace(/\s+/g, "-") !== city).slice(0, 12).map((otherCity) => (
              <Link
                key={otherCity}
                to={`/services/${service}/${otherCity.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-4 py-2 bg-white rounded-full text-sm text-gray-700 hover:bg-forest/5 hover:text-forest transition-colors border border-stone-200"
              >
                {serviceName} in {otherCity}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-forest text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl mb-4">
            Ready to Hire {serviceName} in {cityName}?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Post your job in minutes and get quotes from trusted professionals.
          </p>
          <Link to="/register?type=customer">
            <Button size="lg" className="bg-sienna hover:bg-sienna-dark rounded-full px-8">
              Get Started Free
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
